import { nextTick } from 'vue';
<template>
    <hts-map
        ref="map"
        :minus-height="minusHeight" />
</template>

<script lang="ts" setup>
const { library, vehicleGps } = $store()

interface IRoute {
  originData: google.maps.LatLngLiteral
  destinationData: google.maps.LatLngLiteral
  waypointsData: {
    location: google.maps.LatLngLiteral
    node?: string
  }[]
  options?: {
    originLocationName?: string
    destinationLocationName?: string
    sameOriginDestination?: boolean
  }
}
const props = defineProps({
  mapStyle: {
    type: String,
    default: ''
  },
  centerPosition: Object as PropType<any>,
  centerMarker: Boolean,
  route: {
    type: Object as PropType<IRoute>,
    default: null
  },
  showLocation: {
    type: Boolean,
    default: true
  },
  minusHeight: {
    type: Number,
    default: 0
  }
})

const emits = defineEmits(['click', 'clickMarker'])

const { centerPosition, centerMarker, route, showLocation } = toRefs(props)
const { $eventBus } = useNuxtApp()
const vehicles = computed<any[]>({
  get () {
    return vehicleGps.vehicles
  },
  set (val: any[]) {
    vehicleGps.setVehicles(val)
  }
})
const map: any = ref(null)
const company = computed(() => $store().system.getCompany)
const nodeList = computed(() => {
  return library.locations
})

onMounted(() => {
  map.value.ready().then(() => {})
  if (showLocation.value) {
    renderNodeList()
  }

  showAllVehicles()
  renderRoute()
  $eventBus.on('vehicleChange', handleListenChangeVehicle)
})

onUnmounted(() => {
  $eventBus.off('vehicleChange', handleListenChangeVehicle)
})

watch(
  () => centerMarker.value,
  () => {
    map.value &&
      map.value.ready().then(() => {
        if (centerPosition?.value) {
          map.value.createAdvanceMarker(centerPosition.value as google.maps.LatLng | google.maps.LatLngLiteral)
        }
      })
  },
  { deep: true }
)

watch(
  () => centerPosition?.value,
  (val: Object) => {
    map.value &&
      map.value.ready().then(() => {
        if (centerPosition?.value) {
          map.value.removeMarkers()
          map.value.createMarker(val as google.maps.LatLng | google.maps.LatLngLiteral)
        }
      })
  },
  { deep: true }
)

watch(
  () => route.value,
  (val: any) => {
    if (!map.value) {
      return
    }
    if (!val) {
      map.value.clearDirection()
      return
    }
    renderRoute()
  },
  { deep: true }
)
const getMarkerContent = (vehicle: any) => {
  let content = `<div><p><strong>${vehicle.licensePlate}</strong></p><p>Tài xế: ${vehicle.driverName || ''}</p>`
  if (vehicle.currentJourney) {
    content += `<p>Chặng: ${vehicle.currentJourney.from} > ${vehicle.currentJourney.to}</p>`
  }
  content += '</div>'
  const infoWindow = new google.maps.InfoWindow({
    content,
    pixelOffset: new google.maps.Size(0, -35)
  })

  const htmlContent = document.createElement('div')
  htmlContent.className = 'hts-marker-vehicle ' + getClass(vehicle)
  const textDiv = document.createElement('div')
  textDiv.className = 'vehicle-marker'
  const text = document.createElement('span')
  htmlContent.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25zM13.5 15h-12v2.625c0 1.035.84 1.875 1.875 1.875h.375a3 3 0 116 0h3a.75.75 0 00.75-.75V15z" />
        <path d="M8.25 19.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0zM15.75 6.75a.75.75 0 00-.75.75v11.25c0 .087.015.17.042.248a3 3 0 015.958.464c.853-.175 1.522-.935 1.464-1.883a18.659 18.659 0 00-3.732-10.104 1.837 1.837 0 00-1.47-.725H15.75z" />
        <path d="M19.5 19.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
      </svg>`

  text.className = 'vehicle-info'
  text.textContent = vehicle.licensePlate || ''
  textDiv.appendChild(text)
  if (vehicle?.gpsInfo?.speed) {
    const textSpeed = document.createElement('span')
    textSpeed.className = 'vehicle-speed'
    textSpeed.textContent = vehicle.gpsInfo?.speed + ' Km/h'
    textDiv.appendChild(textSpeed)
  }
  htmlContent.appendChild(textDiv)
  return htmlContent
}
const createInfoWindow = async (vehicle: any) => {
  let content = `<div><p><strong>${vehicle.licensePlate}</strong></p><p>Tài xế: ${vehicle.driverName || ''}</p>`
  if (vehicle.currentJourney) {
    content += `<p>Chặng: ${vehicle.currentJourney.from} > ${vehicle.currentJourney.to}</p>`
  }
  content += '</div>'
  const infoWindow = new google.maps.InfoWindow({
    content,
    pixelOffset: new google.maps.Size(0, -35)
  })

  if (vehicle?.gpsInfo) {
    const markerContent = getMarkerContent(vehicle)
    const marker = await map.value.createAdvanceMarker(vehicle.gpsInfo as google.maps.LatLng | google.maps.LatLngLiteral, markerContent, vehicle)
    marker.addListener('click', () => {
      infoWindow.close()
      infoWindow.open(map, marker)
      map.value.hideInfoWindow()
      map.value.hideClickPositionMarker()
    })
  }

  return infoWindow
}
const getAllVehicles = async () => {
  const { data } = await $api($url.vehicle.vehicle.getGPSAll)
  return data.value?.data || []
}
const getVehicle = async (id: string) => {
  const { data } = await $api($url.vehicle.vehicle.getGPS, { uriParams: { ID: id } })
  return data.value?.data || {}
}
const showVehicle = async (id: string) => {
  removeAdvanceMarkers()
  const vehicle = await getVehicle(id)
  const exists = vehicles.value.findIndex((v: any) => v._id === id)
  if (exists >= 0) {
    vehicles.value[exists].gpsInfo = vehicle.gpsInfo
  }
  createInfoWindow(vehicle)
}
const showAllVehicles = async () => {
  vehicles.value = await getAllVehicles()
  map.value.ready().then(() => {
    console.log('REMOVE ALL VEHICLES')
    removeAdvanceMarkers()
    vehicles.value.forEach((vehicle: any) => {
      createInfoWindow(vehicle)
    })
  })
}

const renderNodeList = (latLngs?: google.maps.LatLngLiteral[]) => {
  if (!map.value) {
    return
  }
  map.value.ready().then(() => {
    nodeList.value.forEach(async (node: any) => {
      const lat = parseFloat(node.lat)
      const lng = parseFloat(node.lng)

      let draw = false
      if (latLngs && Array.isArray(latLngs) && latLngs.length) {
        for (let i = 0; i < latLngs.length; i++) {
          const sLat = latLngs[i].lat
          const sLng = latLngs[i].lng

          if (lat === sLat && sLng === lng) {
            draw = true
          }
        }
      } else {
        draw = true
      }

      if (!draw) {
        return
      }
      const innerHtml = node.newLocation
        ? `
<div><?xml version="1.0" encoding="iso-8859-1"?>
<!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
<svg fill="currentColor" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
	 viewBox="0 0 34.398 34.398" xml:space="preserve">
<g>
	<g id="c28_geolocalization">
		<path d="M17.202,0C10.749,0,5.515,5.197,5.515,11.607c0,3.281,3.218,9.156,3.218,9.156l8.039,13.635l8.386-13.477
			c0,0,3.726-5.605,3.726-9.314C28.883,5.197,23.653,0,17.202,0z M17.147,18.002c-3.695,0-6.688-2.994-6.688-6.693
			c0-3.693,2.993-6.686,6.688-6.686c3.693,0,6.69,2.992,6.69,6.686C23.837,15.008,20.84,18.002,17.147,18.002z"/>
		<polygon points="18.539,7.233 15.898,7.233 15.898,10.242 12.823,10.242 12.823,12.887 15.898,12.887 15.898,15.985
			18.539,15.985 18.539,12.887 21.576,12.887 21.576,10.242 18.539,10.242 		"/>
	</g>
	<g id="Capa_1_146_">
	</g>
</g>
</svg></div>`
        : `<div><svg  xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 256 256" xml:space="preserve">
<defs>
</defs>
<g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
	<path d="M 45 0 C 27.395 0 13.123 14.272 13.123 31.877 c 0 7.86 2.858 15.043 7.573 20.6 L 45 81.101 l 24.304 -28.624 c 4.716 -5.558 7.573 -12.741 7.573 -20.6 C 76.877 14.272 62.605 0 45 0 z M 45 43.889 c -7.24 0 -13.11 -5.869 -13.11 -13.11 c 0 -7.24 5.869 -13.11 13.11 -13.11 s 13.11 5.869 13.11 13.11 C 58.11 38.02 52.24 43.889 45 43.889 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
	<path d="M 58.958 71.559 L 45 82.839 L 31.057 71.556 c -9.329 1.65 -15.682 4.901 -15.682 8.645 c 0 5.412 13.263 9.8 29.625 9.8 c 16.361 0 29.625 -4.388 29.625 -9.8 C 74.625 76.458 68.278 73.209 58.958 71.559 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
</g>
</svg></div>`
      const htmlContent = document.createElement('div')
      htmlContent.className = 'hts-marker text-blue-500' + (node.className || '')
      const text = document.createElement('span')
      const icon = document.createElement('img')
      // icon.src = node.newLocation ? '/assets/svg/alarm' : '/assets/location.png'
      htmlContent.innerHTML = innerHtml
      text.textContent = node.locationName || $t('labels.unknown')
      htmlContent.appendChild(icon)
      htmlContent.appendChild(text)
      const marker = await map.value.createAdvanceMarker(
        {
          lat,
          lng
        } as google.maps.LatLng | google.maps.LatLngLiteral,
        htmlContent
      )
      marker.addListener('click', () => {
        emits('clickMarker', node)
        map.value.hideInfoWindow()
        map.value.hideClickPositionMarker()
      })
    })
  })
}

const renderNodeListInRoute = (latLngs: google.maps.LatLngLiteral[]) => {
  renderNodeList(latLngs)
}

const removeAdvanceMarkers = () => {
  map.value && map.value.removeAdvanceMarkers()
}

const renderRoute = () => {
  if (!route.value) {
    return
  }

  if (!map.value) {
    return
  }

  map.value.ready().then(() => {
    const { originData, destinationData, waypointsData, options } = route.value
    map.value && map.value.drawDirection(originData, destinationData, 'DRIVING', waypointsData || [], options)
  })
}

const getClass = (vehicle: any) => {
  if (vehicle?.gpsInfo?.speed) {
    return 'moving'
  }
  return ''
}

const handleListenChangeVehicle = (vehicle: { [key: string]: any }) => {
  console.log('vehicle', vehicle)
  const exists = map.value.advancedMarkers.findIndex((item: any) => {
    return item._id === vehicle._id
  })
  if (exists < 0) {
    createInfoWindow(vehicle)
    return
  }
  if (!vehicle.gpsInfo.lat || !vehicle.gpsInfo.lng) {
    return
  }
  const advanceMarker = map.value.advancedMarkers[exists]

  const marker = advanceMarker.marker
  marker.content = getMarkerContent({ ...advanceMarker, ...vehicle })
  marker.position = { lat: vehicle.gpsInfo.lat, lng: vehicle.gpsInfo.lng }
}

const ready = async () => {
  let interval: NodeJS.Timeout | undefined
  return await new Promise((resolve) => {
    interval = setInterval(() => {
      map.value.ready().then(() => {
        clearInterval(interval)
        resolve(true)
      })
    }, 100)
  })
}
const setCenter = (vehicle: any) => {
  const exists = map.value.advancedMarkers.findIndex((item: any) => {
    return item._id === vehicle._id
  })
  if (exists < 0) {
    return
  }
  const advanceMarker = map.value.advancedMarkers[exists]
  map.value && map.value.setCenter(advanceMarker.marker.position)
}
defineExpose({ setCenter, ready, renderNodeList, removeAdvanceMarkers, renderNodeListInRoute, showAllVehicles, showVehicle })
</script>
